import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';

import type { ActiveJobPosting } from '@/types/jobPosting.types';

interface JobPostingCardProps {
  posting: ActiveJobPosting;
}

const ROLE_BADGE_COLORS: Record<ActiveJobPosting['role'], string> = {
  armed: 'bg-red-100 text-red-700',
  unarmed: 'bg-blue-100 text-blue-700',
  supervisor: 'bg-purple-100 text-purple-700',
  patrol: 'bg-green-100 text-green-700',
  event: 'bg-amber-100 text-amber-700',
};

export function JobPostingCard({ posting }: JobPostingCardProps) {
  const { t } = useTranslation();

  return (
    <Link
      to={`/jobs/${posting.jobPostingId}`}
      className={cn(
        'flex flex-col rounded-lg border border-gray-200 bg-white p-5 shadow-sm',
        'transition hover:shadow-md hover:border-primary/30',
      )}>
      <div className='mb-3 flex items-start justify-between gap-3'>
        <h3 className='text-xl font-bold text-gray-900'>{posting.title}</h3>
        <span
          className={cn(
            'inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
            ROLE_BADGE_COLORS[posting.role],
          )}>
          {t(`jobPostings.roles.${posting.role}`)}
        </span>
      </div>

      <div className='mb-3 flex items-center gap-1.5 text-sm text-gray-500'>
        <MapPin className='h-4 w-4' />
        <span>{t('jobPostings.location', { city: posting.siteCity, state: posting.siteState })}</span>
      </div>

      {posting.description && <p className='mb-4 line-clamp-3 text-sm text-gray-600'>{posting.description}</p>}

      <span className='mt-auto w-full rounded-lg bg-primary px-4 py-3 text-center text-base font-bold text-white shadow-sm'>
        {t('jobPostings.viewDetails')}
      </span>
    </Link>
  );
}
